import { Observable, of, switchMap } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';

import { params } from '@app/configurations';
import { GoTo } from '@app/core/store/states/router-nav/router-nav-state.actions';
import { RouterPath } from '@app/core/store/states/router-nav/router-nav-state.model';

import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { DefaultGlobalFilters } from '@app/core/store/states/global-filters/global-filters-state.actions';
import { getPortfolioLabelFromParams } from '@app/core/utilities/refiners.utilities';
import { AccountsApiService } from '../../../api/accounts-api.service';
import { Account } from '../../../models/common.models';
import { Currency } from '../../../models/customer.models';
import { RouterNavState } from '../router-nav/router-nav.state';
import { CancelAccountMove, CancelAllAccountMove, GetAllAccounts, RefreshAccounts } from './accounts-state.actions';
import { ACCOUNTS_STATE_TOKEN, AccountsStateModel, FindAccount } from './accounts-state.models';

@Injectable()
@State<AccountsStateModel>({
  name: ACCOUNTS_STATE_TOKEN,
  defaults: {
    total: 0,
    allAccounts: [],
  },
})
export class AccountsState {
  @Selector()
  static hasAccounts({ total }: AccountsStateModel): boolean {
    return total > 0;
  }

  @Selector()
  static allAccounts({ allAccounts }: AccountsStateModel): Account[] {
    return allAccounts;
  }
  @Selector()
  static connectedAccounts({ allAccounts }: AccountsStateModel): Account[] {
    return allAccounts.filter((a) => !a.disconnected);
  }

  @Selector()
  static allAccountsCurrencyMap({ allAccounts }: AccountsStateModel): Record<number, Currency> {
    return (
      allAccounts?.reduce((acc, account: Account) => {
        acc[account.adspertAccountId] = account.currency;
        return acc;
      }, {}) || {}
    );
  }

  @Selector()
  static allAccountsCount({ allAccounts }: AccountsStateModel): number {
    return allAccounts.length;
  }

  @Selector()
  static accountsScheduledForMove({ allAccounts }: AccountsStateModel): Account[] {
    return allAccounts.filter((account, i) => account.customerChangeTarget != null && !account.customerChangeStarted);
  }

  @Selector()
  static accountsCurrentlyMoving({ allAccounts }: AccountsStateModel): Account[] {
    return allAccounts.filter((account, i) => account.customerChangeStarted);
  }

  @Selector([AccountsState.allAccounts, RouterNavState.queryParam(params.ADSPERT_ID)])
  static snoozeMode(allAccounts: Account[], accountID: string): boolean {
    if (!!accountID) {
      return !!FindAccount(allAccounts, accountID)?.snoozed;
    } else {
      return allAccounts.filter((d) => !!d.snoozed).length === allAccounts.length;
    }
  }

  @Selector([AccountsState.allAccounts, CustomerState.canceled, CustomerState.customerCreatedAt])
  static firstSyncPending(allAccounts: Account[], canceled: boolean, createdAt: Date): boolean {
    return (
      !canceled &&
      Date.now() - createdAt.getTime() < 8.64e7 &&
      allAccounts.length === 1 &&
      allAccounts.filter((d) => !d.signupCompleted).length === allAccounts.length
    );
  }

  @Selector([AccountsState.allAccounts, RouterNavState.queryParam(params.ADSPERT_ID)])
  static adpsertAccountStartDate(allAccounts: Account[], accountID: string): Date | null {
    const selectedAccount = FindAccount(allAccounts, accountID);
    return selectedAccount ? new Date(selectedAccount.optimizationStart || selectedAccount.connectedAt) : null;
  }

  @Selector([
    AccountsState.allAccounts,
    RouterNavState.queryParam(params.ADSPERT_ID),
    RouterNavState.queryParam(params.GLOBAL_FILTER_CAMPAIGN_ID),
    RouterNavState.queryParam(params.GLOBAL_FILTER_CAMPAIGN_QUERY),
    RouterNavState.queryParam(params.GLOBAL_FILTER_AD_GROUPS_ID),
    RouterNavState.queryParam(params.GLOBAL_FILTER_CRITERIA_ID),
  ])
  static getActiveAccount(
    allAccounts: Account[],
    adspertId: string,
    campaignId: string,
    campaignQuery: string,
    adGroupId: string,
    criteriaId: string,
  ): Account | undefined {
    if (!adspertId) {
      const portfolioId = getPortfolioLabelFromParams(
        { [params.GLOBAL_FILTER_CAMPAIGN_QUERY]: campaignQuery },
        'portfolio',
      );

      for (let id of [portfolioId, campaignId, adGroupId, criteriaId]) {
        adspertId = id?.split(':')[0];

        if (adspertId) {
          break;
        }
      }
    }

    return adspertId ? FindAccount(allAccounts, adspertId) : void 0;
  }

  static getSelectedAccount(id: string | number | undefined) {
    return createSelector([AccountsState.allAccounts], (allAccounts: Account[]): Account | undefined =>
      FindAccount(allAccounts, id),
    );
  }

  static getSelectedAccountCurrency(id: string | undefined) {
    return createSelector(
      [AccountsState.getSelectedAccount(id)],
      (account?: Account): Currency | undefined => account?.currency || ('' as Currency),
    );
  }

  constructor(
    private aa: AccountsApiService,
    private store: Store,
  ) {}
  @Action(GetAllAccounts)
  getAllAccounts(
    { setState, dispatch }: StateContext<AccountsStateModel>,
    { customerId }: GetAllAccounts,
  ): Observable<unknown> {
    return this.aa.getAllAccounts(customerId).pipe(
      tap((accounts: Account[]) => {
        dispatch(new DefaultGlobalFilters({ showAllAccounts: !accounts.some((account) => account.optimizationStart) }));
        setState(patch({ allAccounts: accounts, total: accounts.length }));
      }),
      switchMap((accounts) => {
        const adspertId = this.store.selectSnapshot(RouterNavState.queryParam(params.ADSPERT_ID));
        const removeAdspertId =
          adspertId && accounts.every(({ adspertAccountId }) => adspertAccountId.toString() !== adspertId);

        return removeAdspertId
          ? dispatch(new GoTo(RouterPath._, { queryParams: { [params.ADSPERT_ID]: null } }))
          : of(null);
      }),
    );
  }

  @Action(RefreshAccounts)
  refresh(
    { setState, dispatch, getState }: StateContext<AccountsStateModel>,
    { customerId, adspertAccountIds }: RefreshAccounts,
  ): Observable<unknown> {
    return this.aa
      .getAllAccounts(
        customerId,
        adspertAccountIds ? { filterValues: { adspertAccountId: adspertAccountIds } } : void 0,
      )
      .pipe(
        map((accounts: Account[]) => {
          const allAccounts = getState().allAccounts.map(
            (account) => accounts.find((a) => a.adspertAccountId === account.adspertAccountId) || account,
          );
          setState(patch({ allAccounts }));
        }),
      );
  }

  @Action(CancelAccountMove)
  cancelMove(
    { dispatch }: StateContext<AccountsStateModel>,
    { adspertAccountId, customerId }: CancelAccountMove,
  ): Observable<unknown> {
    return this.aa
      .cancelAccountMove(adspertAccountId, customerId)
      .pipe(switchMap(() => dispatch([new RefreshAccounts(customerId, [adspertAccountId])])));
  }

  @Action(CancelAllAccountMove)
  cancelAllMove(
    { dispatch }: StateContext<AccountsStateModel>,
    { customerId }: CancelAllAccountMove,
  ): Observable<unknown> {
    const adspertAccountIds = this.store
      .selectSnapshot(AccountsState.accountsScheduledForMove)
      .map((account) => account.adspertAccountId);

    return this.aa
      .cancelAccountMove(adspertAccountIds, customerId)
      .pipe(switchMap(() => dispatch(new RefreshAccounts(customerId, adspertAccountIds))));
  }
}
